import React, { useState, useEffect } from "react";
import {
  FaHome,
  FaBox,
  FaCog,
  FaClipboard,
  FaUsers,
  FaFileAlt,
  FaHeadset,
  FaBoxOpen,
  FaClipboardList,
  FaStore,
  FaSignOutAlt,
  FaChevronDown,
  FaChevronRight,
  FaShoppingCart,
  FaListAlt,
  FaFile,
} from "react-icons/fa";
import { IoIosClose } from "react-icons/io";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../components/Image/kiyo_logo.png";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeRoute, setActiveRoute] = useState(location.pathname);
  const [openDropdowns, setOpenDropdowns] = useState({
    orders: false,
    purchase: false,
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setActiveRoute(location.pathname);
    menuItems.forEach((item) => {
      if (item.dropdown) {
        const isActiveChild = item.dropdown.some(
          (child) => child.route === location.pathname
        );
        if (isActiveChild) {
          setOpenDropdowns((prev) => ({ ...prev, [item.stateKey]: true }));
        }
      }
    });
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    navigate("/login");
  };

  const handleNavigate = (route, isChildRoute = false) => {
    if (isMobile) {
      toggleSidebar();
    }
    setActiveRoute(route);
    if (!isChildRoute) {
      setOpenDropdowns({});
    }
    navigate(route);
  };

  const toggleDropdown = (dropdown) => {
    setOpenDropdowns((prevState) => {
      const newOpenMenus = { ...prevState, [dropdown]: !prevState[dropdown] };
      Object.keys(newOpenMenus).forEach((key) => {
        if (key !== dropdown) newOpenMenus[key] = false;
      });
      return newOpenMenus;
    });
  };

  const userRole = localStorage.getItem("role");

  const menuItems = [
    {
      title: "Orders",
      icon: <FaUsers size={16} />,
      dropdown: [
        {
          title: "New Orders",
          icon: <FaBox size={16} />,
          route: "/sales-executive/order",
        },
        {
          title: "Manage Orders",
          icon: <FaClipboard size={16} />,
          route: "/sales-executive/order/status-details",
        },
      ],
      stateKey: "orders",
    },
    {
      title: "Purchase",
      icon: <FaBoxOpen size={16} />,
      dropdown: [
        {
          title: "Purchase Orders",
          icon: <FaClipboardList size={16} />,
          route: "/sales-executive/purchaseOrder",
        },
        {
          title: "Order Details",
          icon: <FaFileAlt size={16} />,
          route: "/sales-executive/purchaseOrder-details",
        },
        {
          title: "Local Orders",
          icon: <FaStore size={16} />,
          route: "/sales-executive/purchaseOrder/localpurchaseorder",
        },
      ],
      stateKey: "purchase",
    },
    {
      title: "Items Master",
      icon: <FaListAlt size={16} />,
      route: "/sales-executive/items-master",
      stateKey: "items-master",
    },
    {
      title: "Inventory",
      icon: <FaBox size={16} />,
      route: "/sales-executive/warehouse-inventory",
      stateKey: "inventory",
    },
    {
      title: "Clients",
      icon: <FaUsers size={16} />,
      route: "/sales-executive/clients",
      stateKey: "clients",
    },
    {
      title: "Reports",
      icon: <FaFile size={16} />,
      route: "/sales-executive/sales-report",
      stateKey: "reports",
    },
    {
      title: "Logout",
      icon: <FaSignOutAlt size={18} />,
      onClick: handleLogout,
      stateKey: "logout",
    },
  ];

  return (
    <aside
      className={`fixed top-0 left-0 z-50 h-full bg-gray-100 shadow-lg transform transition-all duration-300 ease-in-out ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      } min-w-52 overflow-y-auto`}
      style={{ scrollbarWidth: "none" }}
    >
      <div className="flex items-center justify-between sm:justify-center px-2 pt-2">
        <img
          src={logo}
          alt="Kiyo International"
          className="h-8 sm:h-16 object-contain"
        />
        <button
          className="text-gray-600 hover:text-red-600 transition-transform duration-300 sm:hidden"
          onClick={toggleSidebar}
          aria-label="Toggle Sidebar"
        >
          <IoIosClose size={24} />
        </button>
      </div>

      <nav className="p-2">
        <ul className="space-y-1">
          {menuItems.map((item) => {
            const isActive = item.dropdown
              ? item.dropdown.some((child) => child.route === activeRoute)
              : item.route === activeRoute;

            return (
              <li key={item.stateKey}>
                <button
                  onClick={() =>
                    item.dropdown
                      ? toggleDropdown(item.stateKey)
                      : item.onClick
                      ? item.onClick()
                      : handleNavigate(item.route, false)
                  }
                  className={`w-full grid grid-cols-[min-content_max-content_1fr] cursor-pointer items-center gap-3 p-2 text-sm font-medium transition-all rounded-md duration-200 hover:bg-gray-200 ${
                    isActive
                      ? "bg-gray-200 text-gray-900"
                      : "text-gray-700 hover:text-gray-900"
                  } ${
                    item.title === "Logout"
                      ? "bg-[#d60033] text-white mt-4 hover:bg-red-700"
                      : ""
                  }`}
                  title={item.title}
                >
                  <span
                    className={`text-lg ${
                      item.title === "Logout" ? "text-white" : "text-[#D60033]"
                    } truncate`}
                  >
                    {item.icon}
                  </span>
                  <span className="truncate">{item.title}</span>
                  {item.dropdown &&
                    (openDropdowns[item.stateKey] ? (
                      <FaChevronDown size={12} />
                    ) : (
                      <FaChevronRight size={12} />
                    ))}
                </button>
                {item.dropdown && (
                  <div
                    className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${
                      openDropdowns[item.stateKey] ? "max-h-96" : "max-h-0"
                    }`}
                  >
                    <ul className="pl-6 space-y-1">
                      {item.dropdown.map((subItem, i) => (
                        <li key={subItem.route}>
                          <button
                            onClick={() => handleNavigate(subItem.route, true)}
                            className={`${
                              i === 0 ? "mt-2" : ""
                            } w-full flex items-center gap-3 p-2 cursor-pointer text-sm font-medium rounded-md transition-all duration-200 hover:bg-gray-200 ${
                              activeRoute === subItem.route
                                ? "bg-gray-200 text-gray-900"
                                : "text-gray-700 hover:text-gray-900"
                            }`}
                            title={subItem.title}
                          >
                            <span className="text-lg text-[#D60033]">
                              {subItem.icon}
                            </span>
                            <span className="truncate">{subItem.title}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;
